/* eslint-disable @typescript-eslint/no-explicit-any */
import { isObject } from './type-guards';

/**
 * Deep merges multiple source objects into target object.
 *
 * Recursively merges nested objects. Arrays and primitives are overwritten.
 * Mutates the target object and returns it.
 *
 * @example
 * ```typescript
 * const target = { a: { b: 1, c: 2 } };
 * const source = { a: { c: 3, d: 4 } };
 * merge(target, source);
 * // Result: { a: { b: 1, c: 3, d: 4 } }
 * ```
 */
export const merge = <Type extends object>(target: Type, ...sources: Array<Partial<Type>>): Type => {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        const sourceValue = source[key];
        if (isObject(sourceValue) && isObject(target[key])) {
          target[key] = merge(target[key] as any, sourceValue as any);
        } else {
          (target as any)[key] = sourceValue;
        }
      }
    }
  }
  return merge(target, ...sources);
}
